import { noImage } from '@/commons/constants';
import { FileModel, FileType } from '@/types';
import { getYouTubeVideoIdFromUrl } from '@/utils';
import { DeleteOutlined, ZoomInOutlined } from '@ant-design/icons';
import { Button, Col, Image, message, Row, Space, Tag } from 'antd';
import cx from 'classnames';
import copy from 'copy-to-clipboard';
import { FC } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { BigPlayButton, LoadingSpinner, Player } from 'video-react';

import DeleteFileBtn from './DeleteFileBtn';
import FileItemEditorModal from './FileItemEditorModal';
import FileItemInfo from './FileItemInfo';

const ImageItem = ({
  item,
  onPreview,
}: {
  item: FileModel;
  onPreview?: (value: boolean) => void;
}) => {
  return (
    <Image
      className="uploaded-image__image"
      alt={item.originalName}
      src={item.thumbnail}
      preview={{
        src: item.url,
        maskClassName: 'uploaded-image__image-mask',
        mask: (
          <Space className="image-mask__content" direction="vertical" align="center">
            {onPreview && (
              <div className="content__item" onClick={() => onPreview(true)}>
                <ZoomInOutlined style={{ marginRight: 5 }} />
                Preview
              </div>
            )}

            <div
              className="content__item"
              onClick={(event) => {
                event.stopPropagation();

                copy(item.url);
                message.info(`|${item.name}: Copied|`);
              }}
            >
              Copy
            </div>
          </Space>
        ),
      }}
      fallback={noImage}
      loading="lazy"
    />
  );
};

const VideoItem = ({ item }: { item: FileModel }) => {
  const { url, name } = item;
  return (
    <div className="uploaded-image__video">
      <Player className="video__player" src={url}>
        <LoadingSpinner />
        <BigPlayButton position="center" />
      </Player>
      <div
        className="video__copy"
        onClick={(event) => {
          event.stopPropagation();
          copy(url);
          message.info(`|${name}: Copied|`);
        }}
      >
        Copy
      </div>
    </div>
  );
};

const YoutubeEmbed = ({ item }: { item: any }) => {
  const { url, name } = item;
  const embedId = getYouTubeVideoIdFromUrl(url);

  return (
    <div className="uploaded-image__youtube">
      <iframe
        src={`https://www.youtube.com/embed/${embedId}`}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Embedded youtube"
      />

      <div
        className="youtube__copy"
        onClick={(event) => {
          event.stopPropagation();
          copy(url);
          message.info(`|${name}: Copied|`);
        }}
      >
        Copy
      </div>
    </div>
  );
};

const AudioItem = ({ item }: { item: FileModel }) => {
  const { url, name } = item;
  return (
    <div className="uploaded-image__audio">
      <audio controls>
        <source src={url} />
        <track kind="captions" />
      </audio>
      <div
        className="audio__copy"
        onClick={(event) => {
          event.stopPropagation();
          copy(url);
          message.info(`|${name}: Copied|`);
        }}
      >
        Copy
      </div>
    </div>
  );
};

type ContentProps = {
  item: FileModel;
  selectedItems?: FileModel[];
  onCheckItem?: (item: FileModel, checked: boolean) => void;
  onRemoveItem?: (item: FileModel) => void;
  onDeleteItem?: (item: FileModel) => void;
  onPreview: (value: boolean) => void;
  onSearch?: (values: any) => void;
};

const FileItemContent: FC<ContentProps> = (props: ContentProps) => {
  const { item, selectedItems, onRemoveItem, onCheckItem, onDeleteItem, onPreview, onSearch } =
    props;

  const indexChecked = (selectedItems || []).findIndex((i) => i.id === item.id);
  const checked = indexChecked >= 0;
  const fullName = item.originalName || item.name;
  const infos: string[] = [];

  let name;
  if (fullName) {
    const parts: string[] = fullName.split('.');
    if (parts[1]) {
      infos.push(parts[1].toUpperCase());
    } else {
      infos.push('');
    }

    name = parts[0];
  }
  if (item.width && item.height) {
    infos.push(`${item.width}x${item.height}`);
  }

  return (
    <div>
      <div className="uploaded-image__image-wrapper">
        {item.type === FileType.IMAGE && <ImageItem item={item} onPreview={onPreview} />}
        {item.type === FileType.VIDEO && <VideoItem item={item} />}
        {item.type === FileType.YOUTUBE && <YoutubeEmbed item={item} />}
        {item.type === FileType.AUDIO && <AudioItem item={item} />}
        {onRemoveItem && (
          <div style={{ position: 'absolute', top: 8, left: 8 }}>
            <Button
              type="link"
              danger
              size="small"
              shape="circle"
              className="icon-remove-image"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveItem(item);
              }}
              icon={<DeleteOutlined />}
            />
          </div>
        )}
      </div>
      {!!selectedItems && (
        <div className="uploaded-image__checkbox-container">
          <div
            className={cx('ant-avatar ant-avatar-circle ant-avatar-icon checkbox-content', {
              'checkbox-content--checked': checked,
            })}
            onClick={() => {
              if (onCheckItem) {
                onCheckItem(item, !checked);
              }
            }}
          >
            <span>{indexChecked === -1 ? '' : indexChecked + 1}</span>
          </div>
        </div>
      )}
      {item.tags && !!item.tags.length && (
        <div className="tags-media">
          {item.tags.map((tag) => (
            <Tag
              key={tag}
              onClick={() => {
                if (onSearch) onSearch({ tags: [tag] });
              }}
            >
              {tag}
            </Tag>
          ))}
        </div>
      )}
      <FileItemInfo imageInfo={item} name={name} />
      <div className="uploaded-image__actions">
        {!!infos.length && <p className="uploaded-image__subtitle">{infos.join(' - ')}</p>}
        <Row className="image-btn-actions">
          <FileItemEditorModal item={item} />{' '}
          {!!onDeleteItem && <DeleteFileBtn onDeleteItem={onDeleteItem} item={item} />}
        </Row>
      </div>
    </div>
  );
};

type Props = {
  multiple?: boolean;
  moveItem?: (id: number, atIndex: number) => void;
  findItem?: (id: number) => { item: FileModel; index: number };
} & ContentProps;

export const FileItem: FC<Props> = (props: Props) => {
  const { multiple, item } = props;
  return (
    <Col className="uploaded-image" span={!multiple ? 16 : 8} key={item.id}>
      <FileItemContent {...props} />
    </Col>
  );
};

export const FileItemDraggable: FC<Props> = (props: Props) => {
  const { multiple, item, moveItem, findItem } = props;

  const originalIndex = item?.id && findItem && findItem(item.id)?.index;
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'FileItem',
      item: { id: item.id, originalIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (dragItem, monitor) => {
        const { id: droppedId, originalIndex: index } = dragItem;
        const didDrop = monitor.didDrop();
        if (!didDrop && droppedId && moveItem && index !== undefined) {
          moveItem(droppedId, index);
        }
      },
    }),
    [item.id, originalIndex, moveItem]
  );
  const [, drop] = useDrop(
    () => ({
      accept: 'FileItem',
      hover(dragged: any) {
        if (dragged?.id !== item?.id) {
          const overIndex = item.id && findItem && findItem(item.id)?.index;

          if (moveItem && overIndex !== undefined) {
            moveItem(dragged.id, overIndex);
          }
        }
      },
    }),
    [findItem, moveItem]
  );
  const opacity = isDragging ? 0.4 : 1;
  return (
    <Col
      ref={(node) => {
        if (node) {
          drag(drop(node));
        }
      }}
      className="uploaded-image"
      span={!multiple ? 16 : 8}
      key={item.id}
      style={{ opacity }}
    >
      <FileItemContent {...props} />
    </Col>
  );
};

export default FileItem;
